import { Stack } from '@mui/material';
import { getAnalysisResultsById, getContactById, getInteractionHistoryById, getManualTags, updateContactById } from 'API/contact-management';
import { ProfileBases, ZoomLevels } from 'enums';
import EditContactEmailDialog from 'features/contact-management/contact-details/edit-contact-email-dialog';
import EditContactNameDialog from 'features/contact-management/contact-details/edit-contact-name-dialog';
import EditGenderDialog from 'features/contact-management/contact-details/edit-gender-dialog';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import i18n from 'i18n';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import useErrorStore from 'stores/error-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import { Assessment } from 'types/analysis-result.types';
import { ProfileValues } from 'types/profile.types';
import ContactAnalysisResults from '../features/contact-management/contact-details/contact-analysis-results/contact-analysis-results';
import ContactInfoTable from '../features/contact-management/contact-details/contact-info-table';
import ContactInteractionHistory from '../features/contact-management/contact-details/contact-interaction-history';
import EditManualTagsDialog from '../features/contact-management/contact-details/edit-manual-tags-dialog';
import { Contact, InteractionHistory, ManualTag } from '../features/contact-management/contact-management.types';

const ContactDetails: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const { contactId } = useParams();

  const [contact, setContact] = useState<Contact>();
  const [contactLoading, setContactLoading] = useState(false);
  const [interactionHistories, setInteractionHistories] = useState<InteractionHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<{ assessment?: Assessment; percentiles?: ProfileValues; basis?: ProfileBases }>();
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [tags, setTags] = useState<ManualTag[]>([]);

  const [editContactNameDialog, setEditContactNameDialog] = useState<{ open: boolean; loading: boolean; firstName: string; lastName: string }>({
    open: false,
    loading: false,
    firstName: '',
    lastName: '',
  });
  const [editGenderDialog, setEditGenderDialog] = useState<{ open: boolean; loading: boolean; gender: string }>({
    open: false,
    loading: false,
    gender: '',
  });
  const [editManualTagsDialog, setEditManualTagsDialog] = useState<{ open: boolean; loading: boolean; selectedTags: OptionType[] }>({
    open: false,
    loading: false,
    selectedTags: [],
  });
  const [editContactEmailDialog, setEditContactEmailDialog] = useState<{ open: boolean; loading: boolean; email: string }>({
    open: false,
    loading: false,
    email: '',
  });

  const fetchContact = async () => {
    if (_.isNil(contactId)) return;

    setContactLoading(true);
    const res = await getContactById(contactId);

    if (!res.error) {
      setContact(res);
    } else {
      handleOpenErrorDialog(res.error, '/contact-management/contacts/:contactId', 'get');
    }
    setContactLoading(false);
  };

  const fetchInteractionHistory = async () => {
    if (_.isNil(contactId)) return;

    setHistoryLoading(true);
    const res = await getInteractionHistoryById(contactId);

    if (!res.error) {
      setInteractionHistories(res || []);
    } else {
      handleOpenErrorDialog(res.error, '/contact-management/contacts/:contactId/interaction-history', 'get');
    }
    setHistoryLoading(false);
  };

  const fetchAnalysisResults = async () => {
    if (_.isNil(contactId)) return;

    setAnalysisLoading(true);
    const lang = i18n.languages[0]?.includes('de') ? 'de' : 'en';
    const res = await getAnalysisResultsById(contactId, lang);

    if (!res.error) {
      setAnalysisResults(res);
    } else {
      handleOpenErrorDialog(res.error, '/contact-management/contacts/:contactId/analysis', 'get');
    }
    setAnalysisLoading(false);
  };

  const fetchTags = async () => {
    const res = await getManualTags();
    if (!res.error) {
      setTags(res.tags || []);
    } else {
      setTags([]);
      handleOpenErrorDialog(res.error, '/contact-management/manual-tags', 'get');
    }
  };

  useEffect(() => {
    fetchContact();
    fetchInteractionHistory();
    fetchAnalysisResults();
    fetchTags();
  }, [contactId]);

  /**
   * Edit contact name dialog handlers
   */

  const handleOpenEditContactNameDialog = () => {
    if (_.isNil(contact)) return;

    setEditContactNameDialog((prev) => ({ ...prev, open: true, firstName: contact.firstname || '', lastName: contact.lastname || '' }));
  };

  const handleCloseEditContactNameDialog = () => {
    setEditContactNameDialog({ open: false, loading: false, firstName: '', lastName: '' });
  };

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditContactNameDialog((prev) => ({ ...prev, firstName: event.target.value }));
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditContactNameDialog((prev) => ({ ...prev, lastName: event.target.value }));
  };

  const handleEditName = async () => {
    if (_.isNil(contact)) return;

    setEditContactNameDialog((prev) => ({ ...prev, loading: true }));

    const { firstName, lastName } = editContactNameDialog;

    const res = await updateContactById(contact._id, { firstname: firstName, lastname: lastName });

    if (!res.error) {
      setContact((prev) => ({ ...prev!, firstname: firstName, lastname: lastName }));
      handleCloseEditContactNameDialog();
    } else {
      setEditContactNameDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/contact-management/contacts/:contactId', 'put');
    }
  };

  /**
   * Edit gender dialog
   */

  const handleOpenEditGenderDialog = () => {
    if (_.isNil(contact)) return;

    setEditGenderDialog((prev) => ({ ...prev, open: true, gender: contact.sex }));
  };

  const handleCloseEditGenderDialog = () => {
    setEditGenderDialog({ open: false, loading: false, gender: '' });
  };

  const handleGenderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditGenderDialog((prev) => ({ ...prev, gender: event.target.value }));
  };

  const handleEditGender = async () => {
    if (_.isNil(contact)) return;

    setEditGenderDialog((prev) => ({ ...prev, loading: true }));

    const { gender } = editGenderDialog;

    const res = await updateContactById(contact._id, { sex: gender });

    if (!res.error) {
      setContact((prev) => ({ ...prev!, sex: gender }));
      handleCloseEditGenderDialog();
    } else {
      setEditGenderDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/contact-management/contacts/:contactId', 'put');
    }
  };

  /**
   * Edit manual tags dialog
   */

  const handleOpenEditManualTagsDialog = () => {
    if (_.isNil(contact)) return;

    setEditManualTagsDialog((prev) => ({
      ...prev,
      open: true,
      selectedTags: contact.manualTags?.map((tag) => ({ label: tag.name, value: tag._id })) ?? [],
    }));
  };

  const handleCloseEditManualTagsDialog = () => {
    setEditManualTagsDialog({ open: false, loading: false, selectedTags: [] });
  };

  const handleTagSelectionChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType[]) => {
    setEditManualTagsDialog((prev) => ({ ...prev, open: true, selectedTags: newValue }));
  };

  const handleEditTags = async () => {
    if (_.isNil(contact)) return;

    setEditManualTagsDialog((prev) => ({ ...prev, loading: true }));

    const { selectedTags } = editManualTagsDialog;

    const selectedTagObjects = tags.filter((tag) => selectedTags.some((item) => item.value === tag._id));

    const res = await updateContactById(contact._id, { manualTags: selectedTags.map((tag) => tag.value) });

    if (!res.error) {
      setContact((prev) => ({ ...prev!, manualTags: selectedTagObjects }));
      handleCloseEditManualTagsDialog();
    } else {
      setEditManualTagsDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/contact-management/contacts/:contactId', 'put');
    }
  };

  /**
   * Edit email dialog handlers
   */

  const handleOpenEditContactEmailDialog = () => {
    if (_.isNil(contact)) return;

    setEditContactEmailDialog((prev) => ({ ...prev, open: true, email: contact.email || '' }));
  };

  const handleCloseEditContactEmailDialog = () => {
    setEditContactEmailDialog({ open: false, loading: false, email: '' });
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditContactEmailDialog((prev) => ({ ...prev, email: event.target.value }));
  };

  const handleEditEmail = async () => {
    if (_.isNil(contact)) return;

    setEditContactEmailDialog((prev) => ({ ...prev, loading: true }));

    const { email } = editContactEmailDialog;

    const res = await updateContactById(contact._id, { email });

    if (!res.error) {
      setContact((prev) => ({ ...prev!, email }));
      handleCloseEditContactEmailDialog();
    } else {
      setEditContactEmailDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/contact-management/contacts/:contactId', 'put');
    }
  };

  return (
    <div className={classes.container}>
      <Stack className={classes.content} gap={2} justifyContent={'flex-start'}>
        <Stack className={classes.rowContainer} direction={'row'} gap={2}>
          <div className={cx(classes.row, 'sm')}>
            {contactLoading ? (
              <PaiDotLoader text={t('loadingContact')} />
            ) : (
              <ContactInfoTable
                contact={contact}
                handleOpenEditContactNameDialog={handleOpenEditContactNameDialog}
                handleOpenEditGenderDialog={handleOpenEditGenderDialog}
                handleOpenEditManualTagsDialog={handleOpenEditManualTagsDialog}
                handleOpenEditContactEmailDialog={handleOpenEditContactEmailDialog}
              />
            )}
          </div>
          <div className={cx(classes.row, 'md')}>
            {historyLoading ? <PaiDotLoader text={t('loadingHistory')} /> : <ContactInteractionHistory interactionHistories={interactionHistories} />}
          </div>
          <div className={cx(classes.row, 'sm')}>
            {analysisLoading ? (
              <PaiDotLoader text={t('loadingAnalysis')} />
            ) : (
              <ContactAnalysisResults
                contactName={contact?.firstname}
                contactOccupation=""
                contactAssessment={analysisResults?.assessment}
                contactProfile={analysisResults?.percentiles}
              />
            )}
          </div>
        </Stack>
      </Stack>

      <EditContactNameDialog
        open={editContactNameDialog.open}
        loading={editContactNameDialog.loading}
        firstName={editContactNameDialog.firstName}
        lastName={editContactNameDialog.lastName}
        handleFirstNameChange={handleFirstNameChange}
        handleLastNameChange={handleLastNameChange}
        handleClose={handleCloseEditContactNameDialog}
        handleEditName={handleEditName}
      />
      <EditGenderDialog
        open={editGenderDialog.open}
        loading={editGenderDialog.loading}
        gender={editGenderDialog.gender}
        handleGenderChange={handleGenderChange}
        handleClose={handleCloseEditGenderDialog}
        handleEditGender={handleEditGender}
      />
      <EditManualTagsDialog
        open={editManualTagsDialog.open}
        loading={editManualTagsDialog.loading}
        tags={tags}
        selectedTags={editManualTagsDialog.selectedTags}
        handleTagSelectionChange={handleTagSelectionChange}
        handleClose={handleCloseEditManualTagsDialog}
        handleEditTags={handleEditTags}
      />
      <EditContactEmailDialog
        open={editContactEmailDialog.open}
        loading={editContactEmailDialog.loading}
        email={editContactEmailDialog.email}
        handleEmailChange={handleEmailChange}
        handleClose={handleCloseEditContactEmailDialog}
        handleEditEmail={handleEditEmail}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main,
    height: 'calc(100vh - 60px - 32px)',
    padding: '8px',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  content: {
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '16px',
    height: '100%',
  },
  rowContainer: {
    height: '100%',
  },
  row: {
    height: '100%',
    '&.xs': {
      flexBasis: '12.5%',
    },
    '&.sm': {
      flexBasis: '25%',
    },
    '&.md': {
      flexBasis: '50%',
    },
    '&.lg': {
      flexBasis: '75%)',
    },
    '&.xl': {
      flexBasis: '100%',
    },
  },
}));

export default ContactDetails;
